import React, { useState, useMemo } from 'react';
import { Dialog, DialogTitle, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Document, Page } from 'react-pdf';
import ReactPlayer from 'react-player';
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
  file: File | null;
  fileType: string;
  url?: string; // Add optional URL property for server-side items
}

const PreviewDialog: React.FC<PreviewDialogProps> = ({
  open,
  onClose,
  file,
  fileType,
  url
}) => {
  const [numPages, setNumPages] = useState(0);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  
  React.useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // Determine the source URL to use - either from file or provided url prop
  const sourceUrl = objectUrl || url;

  // Memoize the PDF Document component to prevent unnecessary re-renders
  const PDFDocument = useMemo(() => {
    if (!file && !url) return null;
    const pdfSource = file || url;
    return (
      <Document file={pdfSource} onLoadSuccess={onDocumentLoadSuccess}>
        {Array.from(new Array(numPages), (el, index) => (
          <Page key={`page_${index + 1}`} pageNumber={index + 1} />
        ))}
      </Document>
    );
  }, [file, url, numPages]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md"
      PaperProps={{
        style: {
          width: "auto",
          height: "auto",
          overflow: "auto",
        },
      }}
    >
      <DialogTitle>
        Preview
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {fileType === 'pdf' && (
        <Box
          style={{
            maxHeight: "80vh",
            maxWidth: "100%",
            objectFit: "contain",
            display: "block",
            margin: "auto",
            overflowY: "scroll",
          }}
        >
          {PDFDocument}
        </Box>
      )}

      {fileType === 'photo' && sourceUrl && (
        <img
          src={sourceUrl}
          alt="Preview"
          style={{
            maxHeight: "80vh",
            maxWidth: "100%",
            objectFit: "contain",
            display: "block",
            margin: "auto",
          }}
        />
      )}

      {fileType === 'audio' && sourceUrl && (
        <audio controls style={{ width: 200, height: 50 }}>
          <source src={sourceUrl} type="audio/mpeg" />
          Your browser does not support the audio tag.
        </audio>
      )}

      {fileType === 'video' && sourceUrl && (
        <ReactPlayer
          url={sourceUrl}
          controls={true}
          width="640px"
          height="360px"
        />
      )}
    </Dialog>
  );
};

export default PreviewDialog; 